import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, 
  where, doc, updateDoc, getDoc, Timestamp, orderBy, deleteDoc 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Saleentry() {
  const [lubricants, setLubricants] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // UI & Modal State
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form State
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [rate, setRate] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });

  // 1. Fetch Products (Lubricants)
  useEffect(() => {
    if (!userId) return;
    const qL = query(collection(db, "lubricants"), where("userId", "==", userId));
    const unsub = onSnapshot(qL, (snap) => setLubricants(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [userId]);

  // 2. Fetch Recent Sales
  useEffect(() => {
    if (!userId) return;
    const qS = query(collection(db, "sales"), where("userId", "==", userId), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qS, (snap) => setSales(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [userId]);

  const handleProductSelect = (id) => {
    setProductId(id);
    const selected = lubricants.find(l => l.id === id);
    if (selected) setRate(selected.price || 0); // Auto-fill selling price
  };

  const resetForm = () => {
    setProductId("");
    setQty("");
    setRate("");
    setEditId(null);
    setShowModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId || qty <= 0) return Toast.fire({ icon: 'error', title: 'Invalid quantity' });

    setLoading(true);
    try {
      const saleQty = parseFloat(qty);
      const prodRef = doc(db, "lubricants", productId);
      const totalAmount = saleQty * parseFloat(rate);

      if (editId) {
        // --- EDIT LOGIC ---
        const oldSale = sales.find(s => s.id === editId);
        const prodSnap = await getDoc(prodRef);
        
        // Reverse old stock, apply new stock
        const currentStock = parseFloat(prodSnap.data().stock || 0);
        const adjustedStock = currentStock + oldSale.qty - saleQty;

        await updateDoc(prodRef, { stock: adjustedStock });
        await updateDoc(doc(db, "sales", editId), {
          productId, qty: saleQty, rate: parseFloat(rate), totalAmount, date, updatedAt: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Sale Updated' });
      } else {
        // --- CREATE LOGIC ---
        const prodSnap = await getDoc(prodRef);
        const currentStock = parseFloat(prodSnap.data().stock || 0);

        if (currentStock < saleQty) {
          throw new Error(`Insufficient Stock! Available: ${currentStock}`);
        }

        await addDoc(collection(db, "sales"), {
          userId, productId, qty: saleQty, rate: parseFloat(rate), totalAmount, date, createdAt: Timestamp.now()
        });

        await updateDoc(prodRef, { stock: currentStock - saleQty });
        Toast.fire({ icon: 'success', title: 'Sale Recorded & Stock Reduced' });
      }
      resetForm();
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const deleteSale = async (sale) => {
    const confirm = await Swal.fire({
      title: 'Delete Sale?',
      text: "This will add the quantity back to your stock.",
      icon: 'warning',
      showCancelButton: true,
    });

    if (confirm.isConfirmed) {
      const prodRef = doc(db, "lubricants", sale.productId);
      const prodSnap = await getDoc(prodRef);
      await updateDoc(prodRef, { stock: (prodSnap.data().stock || 0) + sale.qty });
      await deleteDoc(doc(db, "sales", sale.id));
      Toast.fire({ icon: 'success', title: 'Sale Deleted' });
    }
  };

  const openEdit = (sale) => {
    setEditId(sale.id);
    setProductId(sale.productId);
    setQty(sale.qty);
    setRate(sale.rate);
    setDate(sale.date);
    setShowModal(true);
  };

  return (
    <div className="content-wrapper bg-light min-vh-100">
      <section className="content-header py-3 px-4">
        <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border-left-primary">
          <h4 className="m-0 font-weight-bold text-navy"><i className="fas fa-shopping-cart mr-2"></i> Sale Entry</h4>
          <button className="btn btn-primary shadow-sm" onClick={() => { resetForm(); setShowModal(true); }}>
            <i className="fas fa-plus mr-1"></i> New Sale
          </button>
        </div>
      </section>

      <section className="content px-4">
        <div className="card border-0 shadow-sm rounded-lg">
          <div className="card-header bg-white py-3">
            <h6 className="m-0 font-weight-bold text-muted">Recent Sales History</h6>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 text-sm">
                <thead className="bg-light">
                  <tr>
                    <th width="60" className="text-center">Sr.</th>
                    <th>Date</th>
                    <th>Product Name</th>
                    <th className="text-center">Qty</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Total</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.length > 0 ? sales.map((s, i) => (
                    <tr key={s.id}>
                      <td className="text-center text-muted">{i + 1}</td>
                      <td>{s.date}</td>
                      <td className="font-weight-bold">{lubricants.find(l => l.id === s.productId)?.name || 'Unknown'}</td>
                      <td className="text-center">{s.qty}</td>
                      <td className="text-right">₹ {s.rate}</td>
                      <td className="text-right font-weight-bold text-navy">₹ {s.totalAmount.toLocaleString()}</td>
                      <td className="text-center">
                        <button className="btn btn-sm text-info mr-2" onClick={() => openEdit(s)}><i className="fas fa-edit"></i></button>
                        <button className="btn btn-sm text-danger" onClick={() => deleteSale(s)}><i className="fas fa-trash"></i></button>
                      </td>
                    </tr>
                  )) : <tr><td colSpan="7" className="text-center py-4">No sales recorded yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* REACT MODAL (NO JQUERY) */}
      {showModal && (
        <div className="custom-modal-overlay">
          <div className="custom-modal-content animate__animated animate__fadeInDown">
            <div className="modal-header bg-navy text-white py-2">
              <h6 className="modal-title font-weight-bold">{editId ? 'Edit Sale' : 'New Sale Entry'}</h6>
              <button className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-4">
                <label className="small font-weight-bold">Product (Lubricant)</label>
                <select className="form-control mb-3" value={productId} onChange={e => handleProductSelect(e.target.value)} required>
                  <option value="">-- Choose Product --</option>
                  {lubricants.map(l => (
                    <option key={l.id} value={l.id}>{l.name} (Stock: {l.stock})</option>
                  ))}
                </select>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="small font-weight-bold">Quantity</label>
                    <input type="number" step="any" className="form-control" value={qty} onChange={e => setQty(e.target.value)} required />
                  </div>
                  <div className="col-6">
                    <label className="small font-weight-bold">Selling Rate</label>
                    <input type="number" step="any" className="form-control" value={rate} onChange={e => setRate(e.target.value)} required />
                  </div>
                </div>

                <label className="small font-weight-bold">Sale Date</label>
                <input type="date" className="form-control mb-3" value={date} onChange={e => setDate(e.target.value)} required />
                
                <div className="bg-light p-3 rounded text-right">
                  <h5 className="mb-0 text-primary font-weight-bold">Total: ₹ {((parseFloat(qty) || 0) * (parseFloat(rate) || 0)).toLocaleString()}</h5>
                </div>
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-light btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" disabled={loading} className="btn btn-navy btn-sm px-4 shadow">
                  {loading ? 'Processing...' : editId ? 'Update Sale' : 'Confirm Sale'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .text-navy { color: #001f3f !important; }
        .bg-navy { background: #001f3f !important; }
        .btn-navy { background: #001f3f; color: white; }
        .border-left-primary { border-left: 4px solid #007bff; }
        .custom-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 1050;
        }
        .custom-modal-content {
          background: white; width: 450px; border-radius: 10px; overflow: hidden; box-shadow: 0 15px 35px rgba(0,0,0,0.3);
        }
      `}} />
    </div>
  );
}

export default Saleentry;